import dayjs, { Dayjs } from 'dayjs'
import { formatLevel, LevelFormat, PERCENT_LEVEL } from './ChargeLevel.utils'
import { findLastIndex } from 'lodash'
import { match } from 'ts-pattern'
import {
  ChargeForecast,
  ChargeForecastPeriod,
  ChargeForecastPeriodMetric,
} from '@/types'
import { RatePeriod } from '@/types/plugSessions'
import {
  classifyForecastRates,
  formatCents,
  getPeriodCents,
  isSameDisplayedPrice,
  RATE_PERIOD_LABELS,
} from './UpcomingRatesCard.utils'

export type ForecastMetricKey = 'standard' | 'cheapest_rate' | 'smart_charge'

const METRIC_FALLBACK_ORDER: ForecastMetricKey[] = [
  'smart_charge',
  'cheapest_rate',
  'standard',
]

/**
 * Which metric the forecast's selected_plan is charted with. Mirrors the
 * `type` of each label set in the legacy chart's labelsToPlanMapping.
 */
export function getSelectedMetricKey(
  selectedPlan: string | undefined,
): ForecastMetricKey {
  return match(selectedPlan)
    .with('normal', () => 'standard' as const)
    .with(
      'cheapest',
      'green',
      'baseline_readonly',
      () => 'cheapest_rate' as const,
    )
    .otherwise(() => 'smart_charge' as const)
}

/**
 * The period's metric for the selected plan, falling back to whichever metric
 * the backend did send.
 */
export function getPeriodMetric(
  period: ChargeForecastPeriod,
  edge: 'start' | 'end',
  preferred: ForecastMetricKey,
): ChargeForecastPeriodMetric | undefined {
  const keys = [
    preferred,
    ...METRIC_FALLBACK_ORDER.filter((key) => key !== preferred),
  ]
  return keys
    .map((key) => period[`${edge}_${key}_metric`])
    .find((metric) => metric !== undefined && metric !== null)
}

export function formatTime(date: Dayjs) {
  return date.format('h:mm A')
}

export function formatTimeRange(start: Dayjs, end: Dayjs) {
  return `${formatTime(start)} - ${formatTime(end)}`
}

function formatHour(date: Dayjs) {
  return date.minute() === 0 ? date.format('h') : date.format('h:mm')
}

/**
 * "6 – 9 PM" when both ends share a meridiem, otherwise "10 PM – 12 AM".
 */
export function formatHourRange(start: Dayjs, end: Dayjs) {
  const startMeridiem = start.format('A')
  const endMeridiem = end.format('A')
  return startMeridiem === endMeridiem
    ? `${formatHour(start)} – ${formatHour(end)} ${endMeridiem}`
    : `${formatHour(start)} ${startMeridiem} – ${formatHour(
        end,
      )} ${endMeridiem}`
}

export type ChargeSegmentKind =
  | 'charged'
  | 'pausedPast'
  | 'charging'
  | 'paused'
  | 'forecastCharge'
  | 'forecastPause'

export type RateTierRow = {
  period: RatePeriod
  label: string
  timeRange: string
}

export type ChargeSegment = {
  id: string
  kind: ChargeSegmentKind
  isCurrent: boolean
  isCharging: boolean
  title: string
  timeRange: string
  tiers: RateTierRow[]
}

type PeriodGroup = {
  charging: boolean
  // Indexes into chargeForecast.charge_forecast_periods
  indexes: number[]
}

function groupPeriodsByCharging(
  periods: ChargeForecastPeriod[],
  metricKey: ForecastMetricKey,
) {
  return periods.reduce<PeriodGroup[]>((groups, period, index) => {
    const charging =
      getPeriodMetric(period, 'start', metricKey)?.should_charge === true
    const previous = groups[groups.length - 1]
    if (previous && previous.charging === charging) {
      return [
        ...groups.slice(0, -1),
        { ...previous, indexes: [...previous.indexes, index] },
      ]
    }
    return [...groups, { charging, indexes: [index] }]
  }, [])
}

/**
 * Rate tier rows for the given periods, trimmed to the segment's own start and
 * end so a segment never lists hours outside its time range.
 */
function getRateTierRows(
  indexes: number[],
  periods: ChargeForecastPeriod[],
  range: { start: Dayjs; end: Dayjs },
): RateTierRow[] {
  const { periods: ratePeriods } = classifyForecastRates(periods)

  // A run is a stretch at one price, so each row shows the real price of its
  // hours even when a rate change puts two prices in the same tier
  const runs = indexes.reduce<
    { period: RatePeriod; cents: number; start: Dayjs; end: Dayjs }[]
  >((merged, index) => {
    const ratePeriod = ratePeriods[index]
    const cents = getPeriodCents(periods[index])
    const periodStart = dayjs(periods[index].start_datetime)
    const periodEnd = dayjs(periods[index].end_datetime)
    const start = periodStart.isBefore(range.start) ? range.start : periodStart
    const end = periodEnd.isAfter(range.end) ? range.end : periodEnd

    if (!end.isAfter(start)) {
      return merged
    }

    const previous = merged[merged.length - 1]
    if (
      previous &&
      previous.period === ratePeriod &&
      isSameDisplayedPrice(previous.cents, cents)
    ) {
      return [...merged.slice(0, -1), { ...previous, end }]
    }
    return [...merged, { period: ratePeriod, cents, start, end }]
  }, [])

  return runs.map((run) => ({
    period: run.period,
    label: `${RATE_PERIOD_LABELS[run.period]} · ${formatCents(run.cents)}/kWh`,
    timeRange: formatHourRange(run.start, run.end),
  }))
}

export function getChargingWindow(chargeForecast: ChargeForecast) {
  const { schedule_start_time_utc: start, departure_time_utc: end } =
    chargeForecast
  if (!start || !end) {
    return null
  }
  return formatTimeRange(dayjs(start), dayjs(end))
}

/**
 * Splits the forecast into charge and pause segments, up to the last period
 * Optiwatt plans to charge in. Past segments report what the forecast says
 * happened; the current segment uses the car's live battery level.
 */
export function getChargeSegments(args: {
  chargeForecast: ChargeForecast
  now: Dayjs
  // null when the car hasn't reported a battery level
  batteryPercent: number | null
  chargeLimitPercent: number
  isOverride: boolean
  showRateTiers: boolean
  level?: LevelFormat
}): ChargeSegment[] {
  const {
    chargeForecast,
    now,
    batteryPercent,
    chargeLimitPercent,
    level = PERCENT_LEVEL,
    isOverride,
    showRateTiers,
  } = args
  const periods = chargeForecast.charge_forecast_periods ?? []
  const metricKey = getSelectedMetricKey(chargeForecast.selected_plan)
  const groups = groupPeriodsByCharging(periods, metricKey)
  const lastChargingGroupIndex = findLastIndex(
    groups,
    (group) => group.charging,
  )

  if (lastChargingGroupIndex === -1) {
    return []
  }

  const lastChargingIndexes = groups[lastChargingGroupIndex].indexes
  const chargeEnd = dayjs(
    periods[lastChargingIndexes[lastChargingIndexes.length - 1]].end_datetime,
  )

  if (isOverride) {
    const remainingIndexes = periods
      .map((period, index) => ({ period, index }))
      .filter(
        ({ period }) =>
          dayjs(period.end_datetime).isAfter(now) &&
          dayjs(period.start_datetime).isBefore(chargeEnd),
      )
      .map(({ index }) => index)

    return [
      {
        id: 'override',
        kind: 'charging',
        isCurrent: true,
        isCharging: true,
        title:
          level.unit === 'none'
            ? 'Charging'
            : batteryPercent === null
            ? `Charging to ${formatLevel(chargeLimitPercent, level)}`
            : `Charging ${formatLevel(batteryPercent, level)} → ${formatLevel(
                chargeLimitPercent,
                level,
              )}`,
        // A planned window that has already passed reads backwards
        timeRange: chargeEnd.isAfter(now)
          ? `Now - ${formatTime(chargeEnd)}`
          : 'Now',
        tiers: showRateTiers
          ? getRateTierRows(remainingIndexes, periods, {
              start: now.startOf('hour'),
              end: chargeEnd,
            })
          : [],
      },
    ]
  }

  const windowStart = chargeForecast.schedule_start_time_utc
    ? dayjs(chargeForecast.schedule_start_time_utc)
    : null

  return groups
    .slice(0, lastChargingGroupIndex + 1)
    .filter(
      (group) =>
        !windowStart ||
        dayjs(
          periods[group.indexes[group.indexes.length - 1]].end_datetime,
        ).isAfter(windowStart),
    )
    .map((group): ChargeSegment => {
      const firstPeriod = periods[group.indexes[0]]
      const lastPeriod = periods[group.indexes[group.indexes.length - 1]]
      // A segment that began before the charging window is shown from the
      // window's start
      const periodStart = dayjs(firstPeriod.start_datetime)
      const start =
        windowStart && periodStart.isBefore(windowStart)
          ? windowStart
          : periodStart
      const end = dayjs(lastPeriod.end_datetime)
      const startPercent = Math.round(
        getPeriodMetric(firstPeriod, 'start', metricKey)?.battery_percent ??
          batteryPercent ??
          0,
      )
      const endPercent = Math.round(
        getPeriodMetric(lastPeriod, 'end', metricKey)?.battery_percent ??
          startPercent,
      )

      const fmt = (percent: number) => formatLevel(percent, level)
      const bare = level.unit === 'none'
      const timing = match(end.isAfter(now))
        .with(false, () => 'past' as const)
        .otherwise(() =>
          start.isAfter(now) ? ('future' as const) : ('current' as const),
        )

      const kind = match([timing, group.charging] as const)
        .with(['past', true], () => 'charged' as const)
        .with(['past', false], () => 'pausedPast' as const)
        .with(['current', true], () => 'charging' as const)
        .with(['current', false], () => 'paused' as const)
        .with(['future', true], () => 'forecastCharge' as const)
        .with(['future', false], () => 'forecastPause' as const)
        .exhaustive()

      const title = match(kind)
        .with('charged', () =>
          bare
            ? 'Charged'
            : `Charged ${fmt(startPercent)} → ${fmt(endPercent)}`,
        )
        .with('charging', () =>
          bare
            ? 'Charging'
            : batteryPercent === null
            ? `Charging to ${fmt(endPercent)}`
            : `Charging ${fmt(batteryPercent)} → ${fmt(endPercent)}`,
        )
        .with('paused', () =>
          bare || batteryPercent === null
            ? 'Paused'
            : `Paused at ${fmt(batteryPercent)}`,
        )
        .with('pausedPast', () =>
          bare ? 'Paused' : `Paused at ${fmt(startPercent)}`,
        )
        .with('forecastPause', () =>
          bare
            ? 'Forecasted Pause'
            : `Forecasted Pause at ${fmt(startPercent)}`,
        )
        .with('forecastCharge', () =>
          bare
            ? 'Forecasted Charge'
            : `Forecasted Charge ${fmt(startPercent)} → ${fmt(endPercent)}`,
        )
        .exhaustive()

      return {
        id: start.toISOString(),
        kind,
        isCurrent: timing === 'current',
        isCharging: group.charging,
        title,
        timeRange: formatTimeRange(start, end),
        tiers: showRateTiers
          ? getRateTierRows(group.indexes, periods, { start, end })
          : [],
      }
    })
}
